import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react';

import {
  buildEndSessionUrl,
  getAccessToken,
  handleCallback,
  login,
  logout,
  portalAuthConfig,
} from './portal-auth';

interface PortalAuthContextValue {
  /** current access token, or null when unauthenticated */
  token: string | null;
  /** kick off the Keycloak Authorization-Code + PKCE redirect */
  login: () => Promise<void>;
  /** complete the `/callback` round-trip; resolves true on success */
  handleCallback: () => Promise<boolean>;
  /** clear the local token AND end the Keycloak SSO session (redirects away) */
  logout: () => void;
  /** stale/expired token detected by an API call: clear it and re-run the
   *  PKCE redirect (a live Keycloak SSO session bounces straight back). */
  recoverExpiredSession: () => void;
}

const PortalAuthContext = createContext<PortalAuthContextValue | null>(null);

export function PortalAuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => getAccessToken());

  const doLogin = useCallback(() => login(portalAuthConfig()), []);

  const doHandleCallback = useCallback(async () => {
    const ok = await handleCallback(portalAuthConfig());
    setToken(getAccessToken());
    return ok;
  }, []);

  const doLogout = useCallback(() => {
    // Build the end-session URL BEFORE clearing (it wants the stored id_token).
    const endSession = buildEndSessionUrl(portalAuthConfig());
    logout();
    // Deliberately NO setToken(null) here: clearing React state re-renders the
    // AuthGuard, whose token=null effect fires the PKCE auto-login — and ITS
    // window.location assign (async, after the crypto await) lands AFTER ours,
    // winning the navigation race. With a live Keycloak SSO the user was
    // silently logged straight back in ("Sign out does nothing" — owner
    // 2026-08-26). The storage is cleared above and the page navigates away;
    // Keycloak ends the session and returns to '/', where the guard runs a
    // fresh login against the now-dead SSO and lands on the login form.
    window.location.assign(endSession);
  }, []);

  const recoverExpiredSession = useCallback(() => {
    // Clear the stale token; the AuthGuard sees token=null and restarts the
    // PKCE redirect. With a live Keycloak SSO session the round-trip is
    // invisible; a dead one lands on the login page — either way, never a
    // wall of "couldn't load" panels over a dead session.
    logout();
    setToken(null);
  }, []);

  return (
    <PortalAuthContext.Provider
      value={{
        token,
        login: doLogin,
        handleCallback: doHandleCallback,
        logout: doLogout,
        recoverExpiredSession,
      }}
    >
      {children}
    </PortalAuthContext.Provider>
  );
}

export function usePortalAuth(): PortalAuthContextValue {
  const ctx = useContext(PortalAuthContext);
  if (!ctx) {
    throw new Error('usePortalAuth must be used within a PortalAuthProvider');
  }
  return ctx;
}
